package one.healthium.onboarding.ui.screens

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import one.healthium.bundle.components.BundleImage
import one.healthium.onboarding.domain.models.Slide
import one.healthium.onboarding.domain.configs.AdjustingPlanScreenConfig
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.onboarding.ui.components.HighlightItem
import one.healthium.onboarding.ui.components.ProgressSlider
import one.healthium.ui.common.utils.toTextStyle
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme
import kotlin.time.Duration.Companion.seconds

interface AdjustingPlanEventDelegate {
    fun onNextClicked()
}

@Composable
internal fun AdjustingPlanScreen(
    eventDelegate: AdjustingPlanEventDelegate,
    screenConfig: AdjustingPlanScreenConfig,
) {
    UserDataProvider { userState, _ ->
        AppScaffold {
            AdjustingPlanScreenContent(
                screenConfig = screenConfig,
                userState = userState,
                onNextClicked = eventDelegate::onNextClicked,
            )
        }
    }
}

@Composable
internal fun AdjustingPlanScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: AdjustingPlanScreenConfig,
    userState: UserProfileUiModel,
    onNextClicked: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .statusBarsPadding()
            .navigationBarsPadding()
            .padding(horizontal = 24.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        if (screenConfig.slides.isNotEmpty()) {
            SlidesPager(Modifier.weight(1f), screenConfig, userState)
        } else {
            HighlightsPage(Modifier.weight(1f), screenConfig, userState)
        }
        ProgressSlider(slider = screenConfig.slider, onFinished = onNextClicked)
        Spacer(Modifier.height(54.dp))
    }
}

@Composable
private fun SlidesPager(
    modifier: Modifier = Modifier,
    screenConfig: AdjustingPlanScreenConfig,
    userState: UserProfileUiModel
) {
    val scope = rememberCoroutineScope()
    val pagesCount = screenConfig.slides.size + 1
    val pagerState = rememberPagerState(
        pageCount = { pagesCount }
    )

    fun onNextPage() {
        val nextPage = pagerState.currentPage + 1
        if (nextPage < pagerState.pageCount) {
            scope.launch {
                pagerState.animateScrollToPage(nextPage)
            }
        }
    }

    LaunchedEffect(screenConfig.slides) {
        var prevTimeCode = 0f
        for (slide in screenConfig.slides) {
            delay((slide.timeCode - prevTimeCode).toDouble().seconds.inWholeMilliseconds)
            prevTimeCode = slide.timeCode
            onNextPage()
        }
    }

    HorizontalPager(
        modifier = modifier,
        state = pagerState,
        userScrollEnabled = false,
    ) { page ->
        if (page == 0) {
            HighlightsPage(modifier = Modifier.fillMaxSize(), screenConfig = screenConfig, userState = userState)
        } else {
            val slide = screenConfig.slides[page - 1]
            SlidePage(
                slide = slide,
                userState = userState,
            )
        }
    }
}

@Composable
private fun SlidePage(
    slide: Slide,
    userState: UserProfileUiModel,
) {
    Column(
        modifier = Modifier.fillMaxSize(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppText(
            text = slide.title?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.title,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.weight(1f))
        BundleImage(
            modifier = Modifier.fillMaxWidth(),
            name = slide.image,
        )
        Spacer(Modifier.weight(1f))
    }
}

@Composable
private fun HighlightsPage(
    modifier: Modifier = Modifier,
    screenConfig: AdjustingPlanScreenConfig,
    userState: UserProfileUiModel,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppText(
            text = screenConfig.title?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.header,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(24.dp))
        AppText(
            text = screenConfig.subtitle?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.subTitle,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.weight(1f))
        AppText(
            modifier = Modifier.fillMaxWidth(),
            text = screenConfig.benefitsTitle?.bindPlaceholderValues(userState),
            style = screenConfig.benefitsTitleLabel.toTextStyle(AppTheme.textStyles.subTitleSemiBold),
        )
        Spacer(Modifier.height(24.dp))
        screenConfig.items.fastForEach { highlightItem ->
            HighlightItem(
                text = highlightItem.text.bindPlaceholderValues(userState),
                emoji = highlightItem.emoji.value(userState.gender),
                emojiHeight = screenConfig.emojiHeight ?: 40,
                textStyle = screenConfig.textLabel
            )
        }
        Spacer(Modifier.weight(1f))
    }
}
